/**
 * Vidensektionen (/viden) — svarsider der forklarer reglerne, ikke sælger kurser.
 *
 * REGLER FOR DENNE FIL:
 *  1. Hver påstand skal kunne føres tilbage til en kilde i `sources` eller til
 *     Votras eget verificerede datagrundlag (courseGroups.js / courseFacts.js).
 *  2. Ingen priser eller varigheder skrives her. Artiklerne linker til kursussiden,
 *     hvor tallene kommer fra ét sandhedsgrundlag. `renderBabTable` henter BAB-tabellen
 *     direkte fra courseGroups.js.
 *  3. `answer` er svarblokken: det korte, citerbare svar AI-systemer læser først.
 */

export const KNOWLEDGE_UPDATED = "2026-10-02";

const FS = (label, path) => ({ label, url: `https://www.fstyr.dk${path}` });

export const KNOWLEDGE_ARTICLES = [
  {
    slug: "saadan-bliver-du-godsvognmand",
    category: "Vognmand",
    title: "Sådan bliver du godsvognmand, hele vejen fra prøve til tilladelse",
    metaTitle: "Sådan bliver du godsvognmand | Votra",
    description:
      "Trin for trin: bestå godsvognmandsprøven, opfyld kravene til etablering, økonomi, transportleder og vandel, og få din fællesskabstilladelse.",
    readMinutes: 7,
    answer:
      "For at blive godsvognmand skal du bestå den skriftlige godsvognmandsprøve, som Færdselsstyrelsen udarbejder, og derefter opfylde fire øvrige krav til virksomheden: etablering, økonomi (kapitalkrav), løn- og arbejdsvilkår samt vandel. Virksomheden skal desuden udpege en godkendt transportleder. Når det er på plads, ansøger du Færdselsstyrelsen om en fællesskabstilladelse, der koster 1.500 kr. og gælder i op til 10 år.",
    sections: [
      {
        h2: "Hvornår skal du have tilladelse?",
        paragraphs: [
          "Du skal have en fællesskabstilladelse, hvis du transporterer gods for fremmed regning med en lastbil eller et vogntog med en samlet tilladt totalvægt over 3.500 kg i Danmark eller EU. Godskørsel for fremmed regning er kørsel mod betaling, hvor godset ikke tilhører eller på anden måde er knyttet til den registrerede bruger af køretøjet.",
          "Kører du udelukkende med gods, der tilhører din egen virksomhed, er der tale om firmakørsel, og det kræver ikke en fællesskabstilladelse. Grænsen mellem de to er vigtig, og det er netop den slags, prøven spørger ind til.",
        ],
      },
      {
        h2: "Trin 1: Kurset og prøven",
        paragraphs: [
          "Færdselsstyrelsen kræver, at ansøgeren har gennemført et kursus på mindst 144 lektioner, der dækker samtlige emner i bilag 1 til bekendtgørelsen om godskørsel, og har bestået den tilhørende skriftlige prøve. En lektion er 60 minutter inklusive op til 15 minutters pause.",
          "Prøven udarbejdes og tilrettelægges af Færdselsstyrelsen og aflægges i styrelsens elektroniske prøveaflæggelsessystem. Den må kun afholdes af prøveudbydere, der er godkendt af Færdselsstyrelsen, og Votra Danmark ApS er en af dem.",
          "Pensum dækker 11 emneområder: borgerlig ret, handelsret, arbejdsmarkeds- og sociallovgivning, skattelovgivning, forretningsmæssig og økonomisk virksomhedsledelse, adgang til markedet, tekniske standarder og driften, vejtrafiksikkerhed, chaufførvikarvirksomhed, forhandlingsteknik og trafiksikkerhedsledelse.",
        ],
      },
      {
        h2: "Trin 2: De fire krav til virksomheden",
        paragraphs: [
          "Beviset for bestået prøve dækker kun kravet om faglige kvalifikationer. Før tilladelsen kan udstedes, skal virksomheden også opfylde:",
        ],
        list: {
          type: "ul",
          items: [
            "Etablering: en driftscentral, hvorfra hovedaktiviteterne administreres, og hvor tilladelsen og kernedokumenterne opbevares. Virksomheden skal opfylde de skatteretlige krav, være momsregistreret når det kræves, og råde over køretøjer og chauffører i et omfang, der svarer til aktiviteterne.",
            "Økonomi: dokumenteret egenkapital svarende til antallet af tilladelser. Kan dokumenteres med åbningsbalance, revideret årsrapport, revisorerklæring eller garanti fra bank eller forsikringsselskab, og kravet skal være opfyldt i hele gyldighedsperioden.",
            "Løn- og arbejdsvilkår: virksomhedens samlede omkostningsniveau for chaufførerne må ikke afvige væsentligt og entydigt fra de retningsgivende kollektive overenskomster. Der er ikke krav om medlemskab af en arbejdsgiverforening.",
            "Vandel: virksomheden, den administrerende direktør og transportlederen må ikke være dømt for forhold, der kan begrunde fare for nye overtrædelser i erhvervet. Tidligere konkurser i gods- eller buskørselsvirksomheder kan indgå i vurderingen.",
          ],
        },
      },
      {
        h2: "Trin 3: Transportlederen",
        paragraphs: [
          "Virksomheden skal udpege en eller flere godkendte transportledere, der faktisk og vedvarende forestår ledelsen af godskørslen og har reel tilknytning til virksomheden. Det er transportlederen, der skal have bestået vognmandsprøven.",
          "Er transportlederen ansat, skal ansættelseskontrakten sendes med ansøgningen. Er transportlederen ekstern konsulent, skal tjenesteydelseskontrakten med. En konsulent kan være transportleder for op til fire virksomheder med en samlet flåde på højst 50 køretøjer.",
        ],
      },
      {
        h2: "Trin 4: Ansøgning, gebyr og gyldighed",
        paragraphs: [
          "Virksomheden skal være registreret hos Erhvervsstyrelsen og have et CVR-nummer, før der ansøges. Ansøgningen indsendes digitalt med digital signatur.",
          "Gebyret er 1.500 kr. for fællesskabstilladelsen og 1.500 kr. pr. bekræftet kopi, og der udstedes som udgangspunkt én bekræftet kopi pr. indregistreret køretøj. Tilladelsen udstedes for op til 10 år, hvorefter den skal fornyes. Færdselsstyrelsens mål er en afgørelse inden 60 dage, så ansøg i god tid.",
          "Den bekræftede kopi skal altid være i køretøjet, og selve fællesskabstilladelsen skal opbevares på virksomhedens forretningsadresse. Laminér den ikke, så betragtes den som beskadiget.",
        ],
      },
      {
        h2: "De typiske faldgruber",
        paragraphs: [
          "Tre ting overraskede flest: at kapitalkravet skal kunne dokumenteres i hele perioden og ikke kun ved ansøgningen, at transportlederen skal have reel og vedvarende tilknytning (en titel på papiret er ikke nok), og at betingelserne skal være opfyldt løbende, og Færdselsstyrelsen fører tilsyn efter udstedelsen.",
        ],
      },
    ],
    faq: [
      {
        q: "Hvor lang tid tager det at blive godsvognmand?",
        a: "Det afhænger af, hvor hurtigt du er klar til prøven, og hvor hurtigt virksomhedens øvrige krav er på plads. Færdselsstyrelsens mål er en afgørelse på ansøgningen inden 60 dage.",
      },
      {
        q: "Kan jeg gå direkte til godsvognmandsprøven?",
        a: "Ja. Hos Votra gennemfører du kursusmaterialet som selvstudie og går til prøven på den dato, du vælger. Der er ingen mødepligt.",
      },
      {
        q: "Hvad koster en vognmandstilladelse?",
        a: "Gebyret til Færdselsstyrelsen er 1.500 kr. for fællesskabstilladelsen og 1.500 kr. pr. bekræftet kopi. Kursus og prøve betales særskilt, se prisen på kursussiden.",
      },
      {
        q: "Hvor længe gælder tilladelsen?",
        a: "Op til 10 år. Derefter skal den fornyes, og alle betingelser skal fortsat være opfyldt.",
      },
      {
        q: "Skal jeg have lastbilkørekort for at blive godsvognmand?",
        a: "Nej. Vognmandsprøven handler om at drive og lede transportvirksomheden. Kørekort og chaufføruddannelse er krav til den, der fører køretøjet, ikke til transportlederen.",
      },
    ],
    courseSlugs: ["vognmand-lastbil", "vognmand-varebil"],
    sources: [
      FS("Om godskørsel", "/erhverv/gods/om-godskoersel"),
      FS("Fællesskabstilladelsen", "/erhverv/gods/faellesskabstilladelsen"),
      FS("Prøver for godsvognmænd", "/privat/chauffoeruddannelser/uddannelser-i-godskoersel/proever-for-godsvognmaend"),
      { label: "Bekendtgørelse om godskørsel", url: "https://www.retsinformation.dk/eli/lta/2018/1222" },
      { label: "Godskørselsloven", url: "https://www.retsinformation.dk/eli/lta/2020/2168" },
    ],
  },

  {
    slug: "vognmand-lastbil-eller-varebil",
    category: "Vognmand",
    title: "Vognmand til lastbil eller varebil, hvad skal du vælge?",
    metaTitle: "Vognmand lastbil eller varebil? | Votra",
    description:
      "De to vognmandstilladelser dækker ikke hinanden. Se forskellen på vægtgrænser, lovkrav til kursets omfang, pensum og prøveform.",
    readMinutes: 5,
    answer:
      "Vælg lastbilforløbet, hvis du skal køre gods for fremmed regning med lastbil eller vogntog over 3.500 kg, her er lovkravet mindst 144 lektioner efter bilag 1. Vælg varebilforløbet, hvis du kører gods i varebil eller vogntog mellem 2.500 kg og 3.500 kg, her er lovkravet mindst 37 lektioner efter bilag 2. De to tilladelser dækker ikke hinanden, og du kan ikke køre over 3.500 kg på en varebilstilladelse.",
    sections: [
      {
        h2: "Den afgørende forskel: vægten på køretøjet",
        paragraphs: [
          "Grænsen går ved 3.500 kg tilladt totalvægt. Over den grænse kræver godskørsel for fremmed regning en fællesskabstilladelse, og transportlederen skal have bestået godsvognmandsprøven efter bilag 1 til bekendtgørelsen om godskørsel.",
          "Mellem 2.500 kg og 3.500 kg gælder varebilsreglerne. Her er pensum bilag 2, og lovkravet til kursets omfang er mindst 37 lektioner i stedet for 144.",
        ],
      },
      {
        h2: "Hvad er pensumforskellen?",
        paragraphs: [
          "Begge forløb dækker transportkontrakter og CMR, handelsret, arbejdsmarkedsforhold, skat, økonomistyring, adgang til markedet, lastsikring, særligt gods og trafiksikkerhedsledelse.",
          "Lastbilpensum går derudover i dybden med chaufførvikarvirksomhed, forhandlingsteknik, tekniske standarder for køretøjer, kombineret transport, transport af levende dyr og vejtrafiksikkerhed som ledelsesdisciplin. Det er den væsentligste grund til, at kravet er 144 lektioner mod 37.",
        ],
      },
      {
        h2: "Chaufførens bevis er ikke det samme som vognmandens",
        paragraphs: [
          "Vognmandsforløbet kvalificerer virksomhedens transportleder. Chaufføren, der sidder bag rattet, skal i stedet have et varebilschaufføruddannelsesbevis (VUB) eller et chaufføruddannelsesbevis til lastbil.",
          "Kravet om VUB gælder, hvis chaufføren kører gods for fremmed regning, der vejer mere end 11 kg pr. stykgods, i en varebil eller et vogntog mellem 2.500 kg og 3.500 kg. Har du et blåt chaufføruddannelsesbevis påført kategori C, har du automatisk de samme rettigheder som en VUB-indehaver.",
          "VUB-beviset gælder som udgangspunkt i fem år og fornyes med to dages efteruddannelse, hvor der ikke skal aflægges prøve. Beviset skal altid medbringes under kørslen.",
        ],
      },
      {
        h2: "Skal du køre til udlandet?",
        paragraphs: [
          "Kører du gods for fremmed regning med varebil til udlandet, kræver EU's vejpakke en international varebilstilladelse. Det er en selvstændig tilladelse, og det er værd at afklare, før du vælger forløb, og Færdselsstyrelsen beskriver de aktuelle regler på deres side om varebil.",
        ],
      },
      {
        h2: "Sådan vælger de fleste",
        paragraphs: [
          "Starter du med kurérkørsel, pakkedistribution eller håndværkerlogistik i varebil, er varebilsforløbet det rigtige sted at begynde. Er planen lastbil, eller international kørsel med tunge køretøjer, så tag lastbilforløbet med det samme. At tage varebilsprøven først giver ikke merit til lastbilprøven.",
        ],
      },
    ],
    faq: [
      {
        q: "Dækker en varebilstilladelse også lastbil?",
        a: "Nej. Tilladelserne dækker ikke hinanden. Skal du køre gods for fremmed regning over 3.500 kg, skal du have en fællesskabstilladelse og bestå godsvognmandsprøven.",
      },
      {
        q: "Hvad er forskellen på VUB og vognmandsprøven?",
        a: "VUB er chaufførens kvalifikationsbevis og handler om at køre varebilen. Vognmandsprøven er virksomhedens faglige kvalifikation og handler om at drive og lede transportvirksomheden.",
      },
      {
        q: "Hvor mange lektioner kræver loven?",
        a: "Mindst 144 lektioner for lastbil (bilag 1) og mindst 37 lektioner for varebil (bilag 2). En lektion er 60 minutter inklusive op til 15 minutters pause.",
      },
      {
        q: "Hvornår gælder VUB-kravet?",
        a: "Når chaufføren kører gods for fremmed regning over 11 kg pr. stykgods i varebil eller vogntog mellem 2.500 kg og 3.500 kg tilladt totalvægt.",
      },
    ],
    courseSlugs: ["vognmand-lastbil", "vognmand-varebil", "vb-gu"],
    sources: [
      FS("Varebil, erhvervsmæssig godskørsel", "/erhverv/varebil"),
      FS("Varebilsuddannelser", "/privat/chauffoeruddannelser/varebilsuddannelser"),
      FS("Fællesskabstilladelsen", "/erhverv/gods/faellesskabstilladelsen"),
      { label: "Bekendtgørelse om godskørsel (bilag 1 og 2)", url: "https://www.retsinformation.dk/eli/lta/2018/1222" },
    ],
  },

  {
    slug: "transportleder-krav-og-ansvar",
    category: "Vognmand",
    title: "Transportleder: krav, opgaver og ansvar",
    metaTitle: "Transportleder: krav, opgaver og ansvar | Votra",
    description:
      "Transportlederen er virksomhedens faglige ansvarlige. Se kravene til faglige kvalifikationer, reel tilknytning og vandel, og hvad opgaven konkret indeholder.",
    readMinutes: 5,
    answer:
      "En transportleder er den person, der faktisk og vedvarende forestår ledelsen af en transportvirksomheds godskørsel. Transportlederen skal have bestået vognmandsprøven, opfylde vandelskravet og have reel tilknytning til virksomheden. Uden en godkendt transportleder kan virksomheden ikke få eller beholde sin tilladelse.",
    sections: [
      {
        h2: "Hvad laver en transportleder?",
        paragraphs: [
          "Transportlederens opgaver omfatter organisering af virksomhedens transportarbejde, navnlig forvaltning af vedligeholdelse af køretøjerne, kontrol med kontrakter og transportdokumenter, grundlæggende regnskabsføring, fordeling af ladninger og tjenesteydelser på chauffører og køretøjer samt kontrol med sikkerhedsprocedurerne.",
          "Det er altså en reel driftsrolle. Færdselsstyrelsen vurderer konkret, om kravene om faktisk og vedvarende transportledelse samt reel tilknytning er opfyldt.",
        ],
      },
      {
        h2: "De tre krav til personen",
        paragraphs: [],
        list: {
          type: "ol",
          items: [
            "Faglige kvalifikationer: transportlederen skal have bestået den skriftlige vognmandsprøve, der dokumenterer kendskab til reglerne for at drive godsvognmandsvirksomhed.",
            "Reel tilknytning: er transportlederen ansat, skal ansættelseskontrakten med oplysning om stillingsbetegnelse, arbejdstid og aflønning indsendes. Er transportlederen konsulent, skal tjenesteydelseskontrakten indsendes, og den skal specificere opgaver og ansvar samt præcisere, at opgaverne udføres udelukkende i virksomhedens interesse.",
            "Vandel: transportlederen må ikke være dømt for forhold, der kan begrunde nærliggende fare for misbrug af adgangen til at udføre vognmandsvirksomhed. Tidligere konkurser i gods- eller buskørselsvirksomheder kan også indgå i vurderingen.",
          ],
        },
      },
      {
        h2: "Ejer, direktør eller aktionær?",
        paragraphs: [
          "Er transportlederen ejer, bestyrelsesmedlem, direktør eller aktionær med mindst 5 % af virksomheden, er det som udgangspunkt ikke nødvendigt at indsende en kontrakt. Det er den typiske situation for den selvstændige vognmand, der selv er transportleder.",
        ],
      },
      {
        h2: "Ekstern transportleder: grænserne",
        paragraphs: [
          "En konsulent kan lede transportarbejdet for op til fire forskellige virksomheder med en kombineret samlet flåde på højst 50 køretøjer. Det er en hård grænse, og en af de første ting, Færdselsstyrelsen kontrollerer, når en ekstern transportleder tilknyttes flere virksomheder.",
        ],
      },
      {
        h2: "Hvis godkendelsen er tilbagekaldt",
        paragraphs: [
          "Er en transportleders godkendelse tidligere blevet tilbagekaldt, skal der ske rehabilitering, før personen kan godkendes igen. Man kan tidligst godkendes tre år efter afgørelsesdatoen, man skal have bestået den relevante vognmandsprøve efter afgørelsesdatoen, prøven må gerne tages i karensperioden, og de almindelige betingelser om vandel og faktisk og vedvarende ledelse skal være opfyldt.",
        ],
      },
    ],
    faq: [
      {
        q: "Skal transportlederen være ansat i virksomheden?",
        a: "Nej, men der skal være reel tilknytning. Transportlederen kan være ejer, direktør, ansat eller ekstern konsulent, i de to sidstnævnte tilfælde skal kontrakten indsendes til Færdselsstyrelsen.",
      },
      {
        q: "Hvor mange virksomheder må en ekstern transportleder lede?",
        a: "Op til fire virksomheder med en samlet flåde på højst 50 køretøjer.",
      },
      {
        q: "Hvilken prøve skal transportlederen bestå?",
        a: "Godsvognmandsprøven for tilladelser til lastbil over 3.500 kg, eller varebilsvognmandsprøven for varebilstilladelser. Prøven udarbejdes af Færdselsstyrelsen og afholdes hos en godkendt prøveudbyder.",
      },
      {
        q: "Kan en virksomhed have flere transportledere?",
        a: "Ja. Virksomheden kan udpege en eller flere transportledere, der faktisk og vedvarende forestår ledelsen af godskørslen.",
      },
    ],
    courseSlugs: ["vognmand-lastbil", "vognmand-varebil"],
    sources: [
      FS("Fællesskabstilladelsen, transportleder", "/erhverv/gods/faellesskabstilladelsen"),
      FS("Prøver for godsvognmænd", "/privat/chauffoeruddannelser/uddannelser-i-godskoersel/proever-for-godsvognmaend"),
    ],
  },

  {
    slug: "saadan-bliver-du-taxachauffoer",
    category: "Taxa",
    title: "Sådan bliver du taxachauffør i Danmark",
    metaTitle: "Sådan bliver du taxachauffør | Votra",
    description:
      "Kravene til taxachauffør: alder, kørekort, lægeattest og danskkundskaber, plus den lovpligtige taxauddannelse og chaufførkortet, som Færdselsstyrelsen udsteder.",
    readMinutes: 4,
    answer:
      "For at køre taxi i Danmark skal du gennemføre den lovpligtige taxauddannelse og derefter have et chaufførkort udstedt af Færdselsstyrelsen. Du skal være fyldt 21 år, have haft kørekort kategori B i mindst tre år, have en godkendt lægeattest og leve op til Færdselsstyrelsens danskkrav. Uddannelsen omfatter teori, praktik og praktisk køreprøve.",
    sections: [
      {
        h2: "Kravene, før du starter",
        paragraphs: [
          "Adgangskravene er faste, og de kan ikke dispenseres væk af uddannelsesstedet:",
        ],
        list: {
          type: "ul",
          items: [
            "Du skal være fyldt 21 år.",
            "Du skal have haft kørekort kategori B i mindst tre år.",
            "Du skal have en godkendt lægeattest, der dækker helbred, syn og hørelse.",
            "Du skal leve op til Færdselsstyrelsens krav til danskkundskaber.",
            "Der er ofte også krav om ren straffeattest.",
          ],
        },
      },
      {
        h2: "Uddannelsen",
        paragraphs: [
          "Taxauddannelsen er den lovpligtige erhvervsuddannelse for taxachauffører. Forløbet kombinerer teori, praktik og en praktisk køreprøve med bedømmelsesskema. Undervisningen gennemføres på dansk, netop fordi Færdselsstyrelsen stiller krav til danskkundskaber.",
          "Hos Votra udbydes taxauddannelsen som AMU-forløb gennem vores samarbejdspartner UCplus, der afholder undervisningen. Opfylder du betingelserne for AMU-pris, er prisen lavere, det fremgår af prisboksen på kursussiden.",
        ],
      },
      {
        h2: "Chaufførkortet",
        paragraphs: [
          "Chaufførkortet udstedes af Færdselsstyrelsen, ikke af uddannelsesstedet. Du ansøger efter bestået uddannelse. Ud over kursusprisen skal du regne med chaufførkortet og en lægeattest, de aktuelle beløb står på kursussiden under udgifter ud over kursusprisen.",
        ],
      },
      {
        h2: "Taxa, flextrafik eller begge?",
        paragraphs: [
          "Mange taxachauffører kører også flextrafik for et trafikselskab. Det kræver BAB-uddannelserne, som er opdelt i moduler afhængigt af, hvilken type kørsel og hvilke hjælpemidler du skal bruge. Vil du både køre taxi og flextrafik, er det en god idé at planlægge de to forløb sammen.",
        ],
      },
    ],
    faq: [
      {
        q: "Hvor gammel skal man være for at køre taxi?",
        a: "Du skal være fyldt 21 år og have haft kørekort kategori B i mindst tre år.",
      },
      {
        q: "Hvem udsteder chaufførkortet til taxi?",
        a: "Færdselsstyrelsen. Uddannelsesstedet uddanner dig, kortet ansøges hos styrelsen efter bestået uddannelse.",
      },
      {
        q: "Kan taxauddannelsen tages på andre sprog end dansk?",
        a: "Nej. Uddannelsen gennemføres på dansk, fordi Færdselsstyrelsen stiller krav til danskkundskaber.",
      },
      {
        q: "Kan jobcenteret betale taxauddannelsen?",
        a: "Det vurderes af dit jobcenter eller din a-kasse. Votra sender gerne kursusbeskrivelse og pris til din sagsbehandler.",
      },
    ],
    courseSlugs: ["taxa", "bab"],
    sources: [
      FS("Taxi (Færdselsstyrelsen)", "/erhverv/taxi"),
      FS("Taxi-kvalifikationsuddannelsen", "/privat/chauffoeruddannelser/taxi-kvalifikationsuddannelsen"),
    ],
  },

  {
    slug: "bab-moduler-hvilket-skal-du-vaelge",
    category: "Flextrafik",
    title: "BAB-moduler: hvilket skal du vælge?",
    metaTitle: "BAB 1, 2, 3A, 3B og 4, hvilket modul skal du vælge? | Votra",
    description:
      "BAB-uddannelserne til flextrafik er modulopbyggede. Se hvad hvert modul dækker, hvilke moduler der kræver hinanden, og hvornår BAB-4 skal fornyes.",
    readMinutes: 4,
    answer:
      "BAB betyder Befordring af Bevægelseshæmmede og er uddannelserne til flextrafik og offentlig servicetrafik. BAB-1 er introduktionen, BAB-2 handler om sygdoms- og alderssvækkede passagerer, BAB-3A er liftbil, BAB-3B er trappemaskine, og BAB-4 er ajourføring. BAB-3A kræver BAB-1 og BAB-2, BAB-3B kræver BAB-3A, og BAB-4-beviset gælder maksimalt fire år.",
    sections: [
      {
        h2: "Sådan hænger modulerne sammen",
        paragraphs: [
          "Modulerne bygger oven på hinanden. Skal du køre liftbil, skal du først have BAB-1 og BAB-2. Skal du bruge trappemaskine, skal du først have BAB-3A. Det er derfor, mange vælger en samlet pakke frem for at tage modulerne spredt over lang tid.",
        ],
        renderBabTable: true,
      },
      {
        h2: "Hvilket modul passer til din kørsel?",
        paragraphs: [
          "Kører du almindelig flextrafik uden hjælpemidler, er BAB-1 og BAB-2 typisk nok. Kører du med kørestolsbrugere i liftbil, skal du have BAB-3A. Skal du hente passagerer op eller ned ad trapper, skal du have BAB-3B oven på BAB-3A. Har du et BAB-bevis, der er ved at udløbe, er BAB-4 ajourføringen.",
        ],
      },
      {
        h2: "Husk fornyelsen",
        paragraphs: [
          "BAB-4-beviset gælder maksimalt fire år. Ligger din frist tæt på, er det en god idé at booke ajourføringen i god tid, trafikselskaberne kræver gyldigt bevis, før du må køre.",
        ],
      },
      {
        h2: "Evakueringsstol (EVS)",
        paragraphs: [
          "EVS er kurset i brug af evakueringsstol, hvor du lærer at evakuere personer med nedsat mobilitet sikkert ned ad trapper. Det kræver gennemført BAB-3B og er altså et specialkursus oven på BAB-forløbet, ikke en del af det.",
        ],
      },
    ],
    faq: [
      {
        q: "Hvad betyder BAB?",
        a: "Befordring af Bevægelseshæmmede. Det er uddannelserne til flextrafik og offentlig servicetrafik, hvor du kører passagerer med særlige behov.",
      },
      {
        q: "Hvilke BAB-moduler kræver andre moduler først?",
        a: "BAB-3A kræver gennemført BAB-1 og BAB-2. BAB-3B kræver gennemført BAB-3A. BAB-4 kræver BAB-2 eller et BAB-4-bevis, der skal fornyes.",
      },
      {
        q: "Hvor længe gælder BAB-4?",
        a: "Maksimalt fire år. Derefter skal beviset fornyes med ajourføring.",
      },
      {
        q: "Kan jeg tage flere moduler på én gang?",
        a: "Ja. Votra udbyder kombinationerne BAB-1 + BAB-2 og den samlede pakke BAB-1 til BAB-3A.",
      },
    ],
    courseSlugs: ["bab", "evs", "taxa"],
    sources: [
      { label: "Uddannelse af chauffører (Movia Flextrafik)", url: "https://moviatrafik.dk/flexvognmand/krav-til-vognmaend/uddannelse-af-chaufforer" },
      { label: "Flextrafik (Movia)", url: "https://www.moviatrafik.dk/flextrafik" },
    ],
  },

  {
    slug: "chauffoerkort-til-taxi-krav",
    category: "Taxa",
    title: "Chaufførkort til taxi: alle krav og hele ansøgningen",
    metaTitle: "Chaufførkort til taxi: krav og prøver | Votra",
    description:
      "Alder, kørekort, danskkrav, de to prøver, lægeattest, vandel, foto og gebyr, hele vejen fra kvalifikationsuddannelsen til chaufførkortet fra Færdselsstyrelsen.",
    readMinutes: 6,
    answer:
      "Chaufførkortet til taxi udstedes af Færdselsstyrelsen. Du skal bestå den lovpligtige kvalifikationsuddannelse, der efter taxibekendtgørelsen skal vare mindst 74 timer, heraf mindst 6 individuelle køretimer, og som afsluttes med både en teoretisk og en praktisk prøve. Du skal være fyldt 21 år ved de afsluttende prøver, have haft kørekort til kategori B i mindst 3 år og dokumentere dansk på niveau med Prøve i Dansk 2. Derudover skal du opfylde helbredskravene for \"gruppe 2\" i taxibekendtgørelsens bilag 1 samt kravet om vandel og god skik. Gebyret for chaufførkortet er 1.700 kr.",
    sections: [
      {
        h2: "Hvem skal have chaufførkort?",
        paragraphs: [
          "Taxibekendtgørelsen gælder for erhvervsmæssig persontransport med en dansk indregistreret bil, der er indrettet til højst 9 personer inklusive chaufføren. Det dækker taxi, limousine og flexkørsel, altså også en stor del af den kørsel, trafikselskaberne udbyder.",
          "Kortet er personligt og udstedes af Færdselsstyrelsen. Uddannelsesstedet uddanner dig og sender ansøgningen, men det er styrelsen, der træffer afgørelsen.",
        ],
      },
      {
        h2: "Adgangskravene til uddannelsen",
        paragraphs: [
          "Før du kan optages på et godkendt kvalifikationskursus, skal du opfylde følgende:",
        ],
        list: {
          type: "ul",
          items: [
            "Du skal være fyldt 21 år på det tidspunkt, hvor du aflægger de afsluttende prøver.",
            "Du skal have haft kørekort til kategori B i mindst 3 år. Perioder, hvor kørekortet har været frakendt, tæller ikke med.",
            "Du skal forevise dit gyldige, originale kørekort, uddannelsesstedet opbevarer en kopi i mindst 2 år efter forløbet.",
            "Du skal beherske dansk på niveau med Prøve i Dansk 2, jf. taxibekendtgørelsens bilag 4. Det dokumenteres som udgangspunkt med bevis for bestået Prøve i Dansk 2 eller folkeskolens prøve i dansk, 9. klasse.",
          ],
        },
      },
      {
        h2: "Uddannelsens indhold og omfang",
        paragraphs: [
          "Kvalifikationskurset skal vare mindst 74 timer, heraf mindst 6 individuelle køretimer med undervisning i trafiksikkerhed, passagervenlig samt defensiv og energirigtig kørsel. En time er 60 minutter inklusive op til 15 minutters pause.",
          "Emnerne er fastsat i bilag 3 til taxibekendtgørelsen: arbejdsret, færdselslovgivning, energirigtig og passagervenlig kørsel (inkl. GPS), taxilovgivningen, arbejdsmiljø og ergonomi, befordring af særlige grupper, konflikthåndtering og kommunikation, kundeservice samt førstehjælp. Førstehjælpsdelen udgør 11 timer og følger Dansk Førstehjælpsråds undervisningsplaner for \"Færdselsrelateret førstehjælp\" og \"Førstehjælp ved hjertestop\".",
          "Har du gennemført begge førstehjælpskurser inden for de seneste 12 måneder, kan du fritages for den del af undervisningen.",
        ],
      },
      {
        h2: "De to afsluttende prøver",
        paragraphs: [
          "Uddannelsen afsluttes med både en teoretisk og en praktisk prøve. Den teoretiske prøve er udarbejdet af Færdselsstyrelsen og aflægges i styrelsens elektroniske prøveaflæggelsessystem.",
          "Ved den praktiske prøve skal du ved at føre bil sandsynliggøre, at du er kvalificeret til erhvervsmæssig persontransport. Bedømmelsen foretages af en godkendt kørelærer til kategori B, og prøven gennemføres med ekstern censur af en kørelærer, der er ansat på et andet godkendt uddannelsessted.",
          "Bemærk: kørelærerens bedømmelse af den praktiske prøve kan ikke indbringes for Færdselsstyrelsen.",
        ],
      },
      {
        h2: "Ansøgningen om kortet",
        paragraphs: [
          "Ansøgningen indgives digitalt gennem uddannelsesstedet i Færdselsstyrelsens bevisadministrationssystem. Du skal selv afgive samtykke via borger.dk til, at styrelsen indhenter vandels- og helbredsoplysninger.",
          "Ansøgningen skal vedlægges en lægeattest på styrelsens særlige blanket, og attesten må ved indlevering ikke være over tre måneder gammel. Du skal desuden uploade et digitalt foto, der lever op til Rigspolitiets krav til pasfoto, nyt og vellignende, ensartet baggrund, ingen andre personer eller objekter, ikke en selfie, højst 10 MB.",
          "Når styrelsen har fastslået, at du opfylder betingelserne, udstedes et midlertidigt chaufførkort, som du kan bruge indtil det endelige kort kommer. Det midlertidige kort skal medbringes under kørslen og forevises på forlangende.",
        ],
      },
      {
        h2: "Helbred og vandel, det der oftest forsinker sagen",
        paragraphs: [
          "Du skal opfylde helbredskravene for \"gruppe 2\" i taxibekendtgørelsens bilag 1. Lægen vurderer især syn og hørelse, og kortet kan udstedes med vilkår som brille- eller linsepåbud, påbud om høreapparat eller en individuelt fastsat tidsbegrænsning.",
          "Færdselsstyrelsen indhenter straffeattest til offentligt brug og eventuelt udskrift fra Det Centrale Kriminalregister. Den offentlige straffeattest går mindst 10 år tilbage. Strafbare forhold afskærer dig ikke automatisk, styrelsen vurderer forholdets karakter, eventuelle gentagelser, dine personlige forhold og den tid, der er gået.",
          "Har du eller har du haft bopæl i udlandet, skal du selv kontakte politimyndighederne i dine tidligere opholdslande og bede dem sende et originalt eksemplar af straffeattesten direkte til styrelsen med post. Det er den enkeltstående ting, der oftest trækker sagen i længden.",
        ],
      },
      {
        h2: "Gebyr og gyldighed",
        paragraphs: [
          "Gebyret for chaufførkortet er 1.700 kr. til Færdselsstyrelsen, og lægeattesten koster typisk 750 kr. Begge beløb ligger ud over kursusprisen.",
          "Et bestået kvalifikationskursus kan bruges som grundlag for chaufførkort i op til 10 år. Venter du længere med at søge, skal kurset tages igen.",
        ],
      },
    ],
    faq: [
      {
        q: "Hvor lang tid tager taxauddannelsen?",
        a: "Lovkravet er mindst 74 timer, heraf mindst 6 individuelle køretimer. Færdselsstyrelsen beskriver forløbet som to ugers kursus. Se det konkrete antal dage på kursussiden.",
      },
      {
        q: "Hvilke danskkrav er der?",
        a: "Du skal beherske dansk på niveau med Prøve i Dansk 2, jf. taxibekendtgørelsens bilag 4. Det dokumenteres som udgangspunkt med bevis for bestået Prøve i Dansk 2 eller folkeskolens prøve i dansk, 9. klasse.",
      },
      {
        q: "Kan jeg tage uddannelsen, før jeg er 21 år?",
        a: "Du skal være fyldt 21 år på det tidspunkt, hvor du aflægger de afsluttende prøver, og have haft kørekort kategori B i mindst 3 år på samme tidspunkt.",
      },
      {
        q: "Hvad koster chaufførkortet?",
        a: "1.700 kr. i gebyr til Færdselsstyrelsen. Dertil kommer lægeattesten, typisk 750 kr. Kursusprisen er særskilt.",
      },
      {
        q: "Kan jeg køre, mens ansøgningen behandles?",
        a: "Færdselsstyrelsen udsteder et midlertidigt chaufførkort, når det er fastslået, at du opfylder betingelserne. Det kan bruges, indtil du modtager det endelige kort.",
      },
      {
        q: "Hvor længe kan jeg bruge mit kursusbevis?",
        a: "Op til 10 år. Et godkendt kvalifikationskursus gennemført inden for de seneste 10 år opfylder kravet om faglige kvalifikationer.",
      },
    ],
    courseSlugs: ["taxa"],
    sources: [
      FS("Taxi-kvalifikationsuddannelsen", "/privat/chauffoeruddannelser/taxi-kvalifikationsuddannelsen"),
      FS("Krav til chaufførkort", "/erhverv/taxi/taxichauffoerer/krav-til-chauffoerkort"),
      FS("Lægeattest", "/erhverv/taxi/taxichauffoerer/laegeattest"),
      { label: "Taxibekendtgørelsen (BEK nr. 1244 af 26/11/2019)", url: "https://www.retsinformation.dk/eli/lta/2019/1244" },
    ],
  },

  {
    slug: "flextrafik-krav-til-chauffoerer",
    category: "Flextrafik",
    title: "Flextrafik: hvilke krav stiller trafikselskabet til chaufføren?",
    metaTitle: "Krav til flextrafik-chauffører (BAB) | Votra",
    description:
      "Movia kræver BAB 1 og BAB 2 af alle flextrafik-chauffører, og BAB 3 ved liftvogn og trappemaskine. Se også kravet om førerkort og vognmandens ansvar.",
    readMinutes: 5,
    answer:
      "For at køre flextrafik for Movia skal chaufføren have gennemført både BAB 1 (Introduktionskursus til offentlig servicetrafik) og BAB 2 (Befordring af sygdoms- og alderssvækkede passagerer). Skal du køre liftvogn eller befordre borgere med trappemaskine, kræves desuden BAB 3 (Befordring af fysisk handicappede passagerer / Befordring af bevægelseshæmmede), det gælder vogntype 3, 4, 5, 6 og 7. Derudover skal chaufføren have et gyldigt førerkort til erhvervsmæssig personbefordring, som kræver et chaufførkursus godkendt af Færdselsstyrelsen med afsluttende prøve. Det er vognmanden, der er forpligtet til at sikre, at chaufførerne har den krævede uddannelse.",
    sections: [
      {
        h2: "Minimumskravet: BAB 1 + BAB 2",
        paragraphs: [
          "Movia skriver direkte, at alle chauffører, som kører flextrafik, skal have gennemført \"Introduktionskursus til offentlig servicetrafik\" (BAB 1) og \"Befordring af sygdoms- og alderssvækkede passagerer\" (BAB 2). Uden de to moduler må chaufføren ikke køre, uanset vogntype.",
          "Det er derfor, de to moduler ofte købes som pakke: de er indgangsbilletten til al flextrafik.",
        ],
      },
      {
        h2: "Hvornår kræves BAB 3?",
        paragraphs: [
          "Skal du køre liftvogn eller befordre borgere med trappemaskine, kræver Movia desuden \"Befordring af fysisk handicappede passagerer\" eller \"Befordring af bevægelseshæmmede\" (BAB 3). Kravet gælder vognmænd og chauffører, der kører type 3, 4, 5, 6 og 7 vogne.",
          "Hos Votra er BAB 3 delt i BAB-3A (liftbil) og BAB-3B (trappemaskine), og modulerne bygger oven på hinanden: BAB-3A kræver BAB-1 og BAB-2, og BAB-3B kræver BAB-3A.",
        ],
      },
      {
        h2: "Førerkortet er et selvstændigt krav",
        paragraphs: [
          "Ud over BAB-modulerne skal en chauffør i flextrafik have et gyldigt førerkort til erhvervsmæssig personbefordring. For at få det udstedt skal man have gennemført et chaufførkursus, der er godkendt af Færdselsstyrelsen, med undervisning i både teoretiske og praktiske forhold og en afsluttende prøve.",
          "Vær opmærksom på ordene: Movia bruger betegnelsen \"førerkort\", som stammer fra den tidligere lov om taxikørsel. I dag udsteder Færdselsstyrelsen \"chaufførkort\" til erhvervsmæssig persontransport, og det er den lovpligtige taxi-kvalifikationsuddannelse, der er vejen dertil. Et gammelt, gyldigt førerkort tæller fortsat.",
        ],
      },
      {
        h2: "Fornyelse: husk BAB-4",
        paragraphs: [
          "BAB-4 er ajourføringen, og beviset gælder maksimalt fire år. Trafikselskaberne kræver gyldigt bevis, før du må køre, så fristen skal holdes, book ajourføringen i god tid frem for at risikere en pause i kørslen.",
        ],
      },
      {
        h2: "Vognmandens ansvar",
        paragraphs: [
          "Movia er utvetydig: som vognmand er du forpligtet til at sørge for, at dine chauffører har gennemgået den krævede uddannelse. Dokumentationen skal kunne fremvises, og kravene kan afhænge af vogntypen og af den konkrete kontrakt.",
          "Driver du vognmandsforretning med flextrafik, er det derfor en god idé at holde et samlet overblik over chaufførernes beviser og udløbsdatoer.",
        ],
      },
    ],
    faq: [
      {
        q: "Hvilke BAB-moduler skal alle flextrafik-chauffører have?",
        a: "BAB 1 og BAB 2. Movia kræver dem af alle chauffører, der kører flextrafik.",
      },
      {
        q: "Hvornår skal jeg have BAB 3?",
        a: "Når du skal køre liftvogn eller befordre borgere med trappemaskine. Kravet gælder vogntype 3, 4, 5, 6 og 7. Hos Votra dækkes det af BAB-3A og BAB-3B.",
      },
      {
        q: "Er BAB nok til at køre flextrafik?",
        a: "Nej. Du skal også have et gyldigt førerkort til erhvervsmæssig personbefordring, som kræver et chaufførkursus godkendt af Færdselsstyrelsen med afsluttende prøve.",
      },
      {
        q: "Hvem har ansvaret for chaufførernes uddannelse?",
        a: "Vognmanden. Movia skriver, at vognmanden er forpligtet til at sørge for, at chaufførerne har gennemgået den krævede uddannelse.",
      },
      {
        q: "Hvor længe gælder BAB-4?",
        a: "Maksimalt fire år, hvorefter beviset skal fornyes med ajourføring.",
      },
    ],
    courseSlugs: ["bab", "evs", "taxa"],
    sources: [
      { label: "Uddannelse af chauffører (Movia Flextrafik)", url: "https://moviatrafik.dk/flexvognmand/krav-til-vognmaend/uddannelse-af-chaufforer" },
      { label: "Krav til vognmænd (Movia Flextrafik)", url: "https://moviatrafik.dk/flexvognmand/krav-til-vognmaend" },
      FS("Taxi-kvalifikationsuddannelsen", "/privat/chauffoeruddannelser/taxi-kvalifikationsuddannelsen"),
    ],
  },
];

export const getArticle = (slug) => KNOWLEDGE_ARTICLES.find((a) => a.slug === slug);
